import Decimal from 'decimal.js';

/**
 * Công thức tính tiền báo giá — dùng chung cho API (nguồn sự thật) và giao diện (xem trước).
 *
 * Giả định (VND, không có số lẻ):
 *   thành tiền      = round(SL × đơn giá)
 *   chiết khấu      = round(thành tiền × CK% / 100)
 *   tiền sau CK     = thành tiền − chiết khấu
 *   thuế GTGT       = round(tiền sau CK × VAT% / 100)      ← làm tròn theo từng dòng
 *   tổng dòng       = tiền sau CK + thuế GTGT
 *   giá vốn dòng    = round(SL ĐVT chính × giá nhập)        ← giá nhập, khối lượng theo 1 ĐVT CHÍNH (P06 B3)
 * SL ĐVT chính = baseQuantity (dòng bán theo ĐVT phụ, = SL ÷ hệ số); không có thì = SL (dòng ĐVT chính).
 * Làm tròn ROUND_HALF_UP đến 0 chữ số thập phân. Tổng phiếu = cộng các dòng.
 *
 * Đầu vào được làm tròn theo độ chính xác cột DB TRƯỚC khi tính (BUG-08), để tiền khớp đúng
 * với SL/đơn giá đã lưu: SL, khối lượng 3 số lẻ; đơn giá, giá nhập, CK%, VAT% 2 số lẻ.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
const MONEY_DP = 0;

export interface QuoteLineInput {
  quantity: number | string;
  unitPrice: number | string;
  discountPercent?: number | string | null;
  vatRate: number | string;
  costPrice?: number | string | null; // giá nhập / 1 ĐVT chính
  weightKg?: number | string | null; // khối lượng / 1 ĐVT chính
  /** SL quy về ĐVT chính (P06 B3, DEC-073 A). null/undefined = bán theo ĐVT chính */
  baseQuantity?: number | string | null;
}

export interface QuoteLineAmounts {
  amount: number;
  discountAmount: number;
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
  costAmount: number | null;
  totalWeightKg: number | null;
}

export interface QuoteTotals {
  subtotal: number;
  discountTotal: number;
  netTotal: number;
  vatTotal: number;
  grandTotal: number;
  costTotal: number;
  /** Lãi gộp = tiền sau CK − giá vốn (chỉ tính trên các dòng có giá nhập) */
  grossProfit: number;
  /** % lãi gộp trên tiền sau CK, 2 chữ số. null nếu tiền sau CK = 0 */
  grossMarginPercent: number | null;
  totalWeightKg: number;
  /** Số dòng chưa có giá nhập → lãi gộp chưa đầy đủ */
  linesMissingCost: number;
}

const dec = (v: number | string | null | undefined) =>
  v === null || v === undefined || v === '' ? new D(0) : new D(v);

const money = (v: Decimal) => v.toDecimalPlaces(MONEY_DP).toNumber();

/** Số lẻ của các cột trong quote_items (numeric(18,3) / numeric(18,2) / numeric(5,2)). */
export const QTY_DP = 3;
export const PRICE_DP = 2;
export const PERCENT_DP = 2;

/** Làm tròn ROUND_HALF_UP về n số lẻ — giống cách PostgreSQL làm tròn khi ghi vào cột numeric. */
export function roundTo(v: number, dp: number): number {
  return new D(v).toDecimalPlaces(dp).toNumber();
}

const decDp = (v: number | string | null | undefined, dp: number) => dec(v).toDecimalPlaces(dp);

export function calcLine(line: QuoteLineInput): QuoteLineAmounts {
  const qty = decDp(line.quantity, QTY_DP);
  const baseQty =
    line.baseQuantity === null || line.baseQuantity === undefined || line.baseQuantity === '' ? qty : decDp(line.baseQuantity, QTY_DP);
  const amount = qty.mul(decDp(line.unitPrice, PRICE_DP)).toDecimalPlaces(MONEY_DP);
  const discountAmount = amount.mul(decDp(line.discountPercent, PERCENT_DP)).div(100).toDecimalPlaces(MONEY_DP);
  const netAmount = amount.minus(discountAmount);
  const vatAmount = netAmount.mul(decDp(line.vatRate, PERCENT_DP)).div(100).toDecimalPlaces(MONEY_DP);
  const hasCost = line.costPrice !== null && line.costPrice !== undefined && line.costPrice !== '';
  const hasWeight = line.weightKg !== null && line.weightKg !== undefined && line.weightKg !== '';
  return {
    amount: money(amount),
    discountAmount: money(discountAmount),
    netAmount: money(netAmount),
    vatAmount: money(vatAmount),
    totalAmount: money(netAmount.plus(vatAmount)),
    costAmount: hasCost ? money(baseQty.mul(decDp(line.costPrice, PRICE_DP))) : null,
    totalWeightKg: hasWeight ? baseQty.mul(decDp(line.weightKg, QTY_DP)).toDecimalPlaces(3).toNumber() : null,
  };
}

export function calcTotals(lines: QuoteLineInput[]): QuoteTotals {
  let subtotal = new D(0);
  let discount = new D(0);
  let vat = new D(0);
  let cost = new D(0);
  let netWithCost = new D(0);
  let weight = new D(0);
  let missing = 0;
  for (const l of lines) {
    const a = calcLine(l);
    subtotal = subtotal.plus(a.amount);
    discount = discount.plus(a.discountAmount);
    vat = vat.plus(a.vatAmount);
    if (a.costAmount === null) missing++;
    else {
      cost = cost.plus(a.costAmount);
      netWithCost = netWithCost.plus(a.netAmount);
    }
    if (a.totalWeightKg !== null) weight = weight.plus(a.totalWeightKg);
  }
  const net = subtotal.minus(discount);
  const profit = netWithCost.minus(cost);
  return {
    subtotal: money(subtotal),
    discountTotal: money(discount),
    netTotal: money(net),
    vatTotal: money(vat),
    grandTotal: money(net.plus(vat)),
    costTotal: money(cost),
    grossProfit: money(profit),
    grossMarginPercent: netWithCost.isZero() ? null : profit.div(netWithCost).mul(100).toDecimalPlaces(2).toNumber(),
    totalWeightKg: weight.toDecimalPlaces(3).toNumber(),
    linesMissingCost: missing,
  };
}

/**
 * CR-007 (anh Tiến 07/10/2026 "Thêm cột VAT 8%, 10% bên dưới cùng"): tiền thuế GTGT tách theo từng thuế suất, thuế suất tăng dần.
 * Cộng các dòng đã làm tròn theo dòng → tổng các mức = vatTotal của calcTotals.
 */
export function vatBreakdown(lines: QuoteLineInput[]): { vatRate: number; netAmount: number; vatAmount: number }[] {
  const by = new Map<string, { vatRate: number; net: Decimal; vat: Decimal }>();
  for (const l of lines) {
    const a = calcLine(l);
    const rate = decDp(l.vatRate, PERCENT_DP);
    const k = rate.toString();
    const cur = by.get(k) ?? { vatRate: rate.toNumber(), net: new D(0), vat: new D(0) };
    cur.net = cur.net.plus(a.netAmount);
    cur.vat = cur.vat.plus(a.vatAmount);
    by.set(k, cur);
  }
  return [...by.values()].sort((a, b) => a.vatRate - b.vatRate).map((x) => ({ vatRate: x.vatRate, netAmount: money(x.net), vatAmount: money(x.vat) }));
}

/**
 * CR-007 ("tính tổng và hiển thị thêm: tổng số lượng, tổng trọng lượng"): tổng SL theo từng ĐVT (cộng SL khác ĐVT không có nghĩa),
 * giữ thứ tự ĐVT xuất hiện lần đầu. Dòng không tính (VD "Vận chuyển") do nơi gọi lọc trước.
 */
export function quantityByUnit(lines: { quantity: number | string; unit: string | null | undefined }[]): { unit: string; quantity: number }[] {
  const by = new Map<string, { unit: string; qty: Decimal }>();
  for (const l of lines) {
    const unit = (l.unit ?? '').trim();
    const k = unit.toLowerCase();
    const cur = by.get(k) ?? { unit, qty: new D(0) };
    cur.qty = cur.qty.plus(decDp(l.quantity, QTY_DP));
    by.set(k, cur);
  }
  return [...by.values()].map((x) => ({ unit: x.unit, quantity: x.qty.toDecimalPlaces(QTY_DP).toNumber() }));
}

/**
 * DEC-297 (anh Tiến 12/10/2026): lãi so theo giá mua CHƯA VAT. Giá nhập Asia là giá đã gồm VAT
 * (mã "mua không VAT" thì giá đó chính là giá mua). VD 1.066.500 có VAT 10% → 969.545 chưa VAT.
 * VND không số lẻ, ROUND_HALF_UP. vatRate ≤ 0 → giữ nguyên.
 */
export function purchaseNetOfVat(gross: number | string, vatRate: number | string | null | undefined): number {
  const r = dec(vatRate);
  return money(r.gt(0) ? dec(gross).mul(100).div(r.plus(100)) : dec(gross));
}

/** Giá mua có VAT để hiện cạnh giá chưa VAT (969.545 / 1.066.500). vatRate ≤ 0 → giữ nguyên. */
export function purchaseGrossOfVat(net: number | string, vatRate: number | string | null | undefined): number {
  const r = dec(vatRate);
  return money(r.gt(0) ? dec(net).mul(r.plus(100)).div(100) : dec(net));
}

/** Hoa hồng CTV (DEC-375) = round(doanh số chưa VAT × % / 100), ROUND_HALF_UP, VND không số lẻ. */
export function commissionAmount(revenue: number | string, pct: number | string): number {
  return decDp(revenue, MONEY_DP).times(decDp(pct, PERCENT_DP)).dividedBy(100).toDecimalPlaces(MONEY_DP).toNumber();
}

/**
 * Đợt đề nghị thanh toán của chủ đầu tư (DEC-541). VND không số lẻ, ROUND_HALF_UP; đầu vào làm tròn theo cột DB.
 *   thuế GTGT        = round(giá trị nghiệm thu × VAT% / 100)
 *   thu hồi tạm ứng  = min(tạm ứng còn lại, round((giá trị + VAT) × % tạm ứng / 100))
 *   giữ lại bảo hành = round((giá trị + VAT) × % bảo hành / 100)
 *   đề nghị thanh toán = giá trị + VAT − thu hồi tạm ứng − giữ lại bảo hành
 */
export interface OwnerClaimInput {
  acceptedValue: number | string;
  vatRate: number | string;
  advancePct: number | string;
  retentionPct: number | string;
  /** Tạm ứng đã nhận chưa thu hồi (đ) */
  advanceOutstanding: number | string;
}
export interface OwnerClaimAmounts { vatAmount: number; grossValue: number; advanceRecovery: number; retentionAmount: number; amountDue: number }

export function ownerClaimAmounts(i: OwnerClaimInput): OwnerClaimAmounts {
  const value = decDp(i.acceptedValue, MONEY_DP);
  const vat = value.times(decDp(i.vatRate, PERCENT_DP)).dividedBy(100).toDecimalPlaces(MONEY_DP);
  const gross = value.plus(vat);
  const recoveryRaw = gross.times(decDp(i.advancePct, PERCENT_DP)).dividedBy(100).toDecimalPlaces(MONEY_DP);
  const outstanding = D.max(0, decDp(i.advanceOutstanding, MONEY_DP));
  const recovery = D.min(recoveryRaw, outstanding);
  const retention = gross.times(decDp(i.retentionPct, PERCENT_DP)).dividedBy(100).toDecimalPlaces(MONEY_DP);
  return { vatAmount: money(vat), grossValue: money(gross), advanceRecovery: money(recovery), retentionAmount: money(retention), amountDue: money(gross.minus(recovery).minus(retention)) };
}

/** Tạm ứng theo hợp đồng = round(giá trị HĐ có VAT × % tạm ứng / 100); giá trị HĐ có VAT = HĐ + round(HĐ × VAT%) */
/** Mốc thu tiền CĐT theo giai đoạn (DEC-577): giá trị nghiệm thu gợi ý = giá trị HĐ chưa VAT × % mốc, làm tròn đồng */
export function ownerMilestoneValue(contractValue: number | string, pct: number | string): number {
  return money(decDp(contractValue, MONEY_DP).times(decDp(pct, PERCENT_DP)).dividedBy(100));
}

export function ownerAdvanceAmount(contractValue: number | string, vatRate: number | string, advancePct: number | string): number {
  const v = decDp(contractValue, MONEY_DP);
  const gross = v.plus(v.times(decDp(vatRate, PERCENT_DP)).dividedBy(100).toDecimalPlaces(MONEY_DP));
  return money(gross.times(decDp(advancePct, PERCENT_DP)).dividedBy(100));
}
